import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { IconType } from "react-icons";
import { LucideIcon } from "lucide-react";

export type FeatureCardProps = {
  icon: IconType | LucideIcon;
  label: string;
  title: string;
  description: string;
  badgeColor: string;
  onCardClick: (question: string) => void;
  className?: string;
};

const FeatureCard = ({
  icon: Icon,
  title,
  description,
  badgeColor,
  onCardClick,
  className = "",
  disabled = false,
}: FeatureCardProps & { disabled?: boolean }) => (
  <Card
    className={`w-full h-full ${
      disabled ? "cursor-not-allowed" : "cursor-pointer"
    } ${className} bg-transparent hover:bg-white`}
    onClick={disabled ? undefined : () => onCardClick(description)}
  >
    <CardHeader className="block pb-0">
      {/* Minimal padding-bottom */}
      <div
        className={`${badgeColor} inline-flex items-center space-x-2 rounded-full text-[10px] font-semibold`}
      >
        <Icon className="w-4 h-4" size={10} />
        <span className="text-[13px] font-medium text-brand-primary-new">{title}</span>
      </div>
    </CardHeader>
    <CardContent>
      <p className="text-[#617390] text-xs leading-tight">
        {/* Reduced line-height */}
        {description}
      </p>
    </CardContent>
  </Card>
);

export function InsightChatCarousel({
  onCardClick,
  slides,
  disableClick = false,
}: {
  onCardClick: (question: string) => void;
  slides?: Array<Array<FeatureCardProps>>;
  disableClick?: boolean;
}) {
  return (
    <Carousel className="w-full max-w-4xl">
      <CarouselContent className={"mt-20"}>
        {slides?.map((item, index) => (
          <CarouselItem key={index}>
            <div className={`grid grid-cols-2`}>
              {item.map((data, idx) => (
                <FeatureCard
                  key={idx}
                  {...data}
                  onCardClick={onCardClick}
                  className={`shadow-none rounded-none border-none 
                    first:!border first:!border-border first:!border-solid first:!border-b-transparent 
                    last:!border last:!border-border last:!border-solid 
                    first:rounded-tl-[16px]  /* Top-left card */
                    [&:nth-child(2)]:rounded-tr-[16px]  /* Top-right card */
                    [&:nth-child(3)]:rounded-bl-[16px]  /* Bottom-left card */
                    last:rounded-br-[16px]   /* Bottom-right card */
                    last:!border-l-transparent 
                    [&:nth-child(2)]:!border-r [&:nth-child(2)]:!border-r-border [&:nth-child(2)]:!border-solid [&:nth-child(2)]:!border-b-transparent [&:nth-child(2)]:!border-l-transparent 
                    [&:nth-child(3)]:!border-l [&:nth-child(3)]:!border-l-border [&:nth-child(3)]:!border-solid`}
                  disabled={disableClick}
                />
              ))}
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
}
